import { SubmoduleScopeType } from '@api/company';
import { Data, SubmoduleCatalog } from '@hooks/api/useUserModulesById';
import {
  AppAccess,
  AppAccessFilter,
  AppAccessType,
  DashboardSelectorModuleItem,
  DashboardSelectorSubmoduleItem,
} from '../types/dashboardSelector';
import { PvxPartnerDashboardsResponse } from '@api/types/pvxPartners';

const hasFullAccess = (
  accessType?: string | null,
  scopeType?: SubmoduleScopeType | null,
): boolean => accessType === 'ALL' && scopeType === SubmoduleScopeType.GLOBAL;

const buildAppAccess = (submodule: SubmoduleCatalog, filters: AppAccessFilter[]): AppAccess => {
  if (hasFullAccess(submodule.accessType, submodule.scopeType)) {
    return { type: AppAccessType.FULL_ACCESS };
  }
  const isDemoAccess =
    filters.length === 1 && filters[0].filterValue === process.env.REACT_APP_DEMO_GAME_ID;

  if (isDemoAccess) {
    return { type: AppAccessType.DEMO, filters: [filters[0]] };
  }
  return {
    type: AppAccessType.CUSTOM,
    filters,
  };
};

export const buildCatalogSubmoduleMap = (catalog: DashboardSelectorModuleItem[]) => {
  const catalogSubmoduleMap = new Map<string, DashboardSelectorSubmoduleItem>();
  for (const module of catalog) {
    for (const submodule of module.submodules) {
      catalogSubmoduleMap.set(submodule.submoduleId, {
        ...submodule,
        moduleId: module.id,
      });
    }
  }

  return catalogSubmoduleMap;
};

export const parseInitialUserModuleAccess = (
  apiData: Data[],
  catalog: DashboardSelectorModuleItem[],
): DashboardSelectorSubmoduleItem[] => {
  const catalogSubmoduleMap = buildCatalogSubmoduleMap(catalog);
  const selectedSubmodules: DashboardSelectorSubmoduleItem[] = [];

  for (const module of apiData) {
    for (const submodule of module.submoduleCatalogs) {
      const catalogSubmodule = catalogSubmoduleMap.get(submodule.id);
      if (!catalogSubmodule) {
        continue;
      }

      const filters: AppAccessFilter[] = submodule.submoduleFilters.flatMap((filterGroup) =>
        (filterGroup.submoduleFilterRoles ?? []).map((role) => ({
          rowId: role.id,
          submoduleFilterId: filterGroup.id,
          filterValue: role.filterValue,
        })),
      );

      const appAccess = buildAppAccess(submodule, filters);

      selectedSubmodules.push({
        ...catalogSubmodule,
        submoduleId: submodule.id,
        moduleId: module.id,
        membershipId: submodule.membershipId,
        name: catalogSubmodule.name || submodule.name,
        accessType: submodule.accessType,
        scopeType: submodule.scopeType,
        appAccess: appAccess,
      });
    }
  }

  return selectedSubmodules;
};

export const parsePartnerSubmodules = (
  dashboardData: PvxPartnerDashboardsResponse,
  catalog: DashboardSelectorModuleItem[],
): DashboardSelectorSubmoduleItem[] => {
  const catalogMap = buildCatalogSubmoduleMap(catalog);

  return dashboardData.submodules.flatMap((apiSubmodule) => {
    const catalogSubmodule = catalogMap.get(apiSubmodule.submoduleId);
    if (!catalogSubmodule) return [];
    return [{ ...catalogSubmodule, editable: apiSubmodule.editable }];
  });
};
